/* F&P product gallery (V10.2). Desktop: the selected colour's photos in a two-up grid of even frames beside a
   sticky details column; a "+" cursor marks a photo that opens full screen. Below 990px: a native swipe track
   that follows the finger, with a counter, a progress line and a strip of thumbnails. A colour change swaps the
   set with the homepage wipe. Full screen: the large file fades in; a click zooms to it at full size and the
   pointer pans (an instant switch, never an animated zoom). Reduced motion: instant changes, no wipe. */
const reduce = matchMedia('(prefers-reduced-motion: reduce)');
const stacked = matchMedia('(min-width: 990px)');
const handleize = (v) => String(v || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

class PdpGallery extends HTMLElement {
  connectedCallback() {
    if (this.ready) return; this.ready = true;
    this.track = this.querySelector('[data-fp-track]');
    this.figures = [...this.querySelectorAll('[data-fp-media]')];
    this.thumbs = new Map([...this.querySelectorAll('[data-fp-thumb]')].map((b) => [b.dataset.fpThumb, b]));
    this.progress = this.querySelector('[data-fp-progress] > span');
    this.countEl = this.querySelector('[data-fp-count]');
    this.currentEl = this.querySelector('[data-fp-current]');
    this.totalEl = this.querySelector('[data-fp-total]');
    this.lightbox = this.querySelector('[data-fp-lightbox]');
    this.token = 0;
    this.index = 0;

    this.thumbs.forEach((btn, id) => btn.addEventListener('click', () => this.goTo(this.visible().findIndex((f) => f.dataset.fpMediaId === id))));
    this.querySelectorAll('[data-fp-enlarge]').forEach((btn) => btn.addEventListener('click', () => this.openLightbox(btn.closest('[data-fp-media]'))));

    // swipe track (below 990px): progress line and counter follow the finger
    let queued = false;
    this.track?.addEventListener('scroll', () => {
      if (queued || stacked.matches) return; queued = true;
      requestAnimationFrame(() => { queued = false; this.syncTrack(); });
    }, { passive: true });
    this.track?.addEventListener('scrollend', () => this.warm(this.index + 1));

    // stacked (desktop): the photo in view drives the index
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver((entries) => {
        if (!stacked.matches) return;
        for (const e of entries) if (e.isIntersecting && e.intersectionRatio >= 0.5) this.setIndex(this.visible().indexOf(e.target));
      }, { threshold: [0.5] });
      this.figures.forEach((f) => this.io.observe(f));
    }
    stacked.addEventListener('change', () => { this.track.scrollLeft = 0; this.setIndex(0); });
    this.setupLightbox();
    this.setupCursor();
    this.refresh();
    if ('requestIdleCallback' in window) requestIdleCallback(() => this.warm(1)); else setTimeout(() => this.warm(1), 1500);
  }

  visible() { return this.figures.filter((f) => !f.hidden); }

  refresh() {
    const n = this.visible().length;
    if (this.totalEl) this.totalEl.textContent = n;
    this.toggleAttribute('data-single', n <= 1);
    // two-up grid: an odd number of photos opens with one wide photo
    this.dataset.count = n;
    this.toggleAttribute('data-odd', n > 1 && n % 2 === 1);
    if (this.progress) this.progress.style.width = `${100 / Math.max(n, 1)}%`;
    this.figures.forEach((f) => { const t = this.thumbs.get(f.dataset.fpMediaId); if (t) t.hidden = f.hidden; });
    this.setIndex(0);
    this.syncTrack();
  }

  setIndex(i) {
    const vis = this.visible();
    if (i < 0 || i >= vis.length) return;
    this.index = i;
    if (this.currentEl) this.currentEl.textContent = i + 1;
    this.thumbs.forEach((t) => t.removeAttribute('aria-current'));
    this.thumbs.get(vis[i].dataset.fpMediaId)?.setAttribute('aria-current', 'true');
    vis.forEach((f, k) => f.setAttribute('aria-label', `${k + 1} of ${vis.length}`));
  }

  syncTrack() {
    if (!this.track || stacked.matches) return;
    const n = this.visible().length; const w = this.track.clientWidth || 1;
    const pos = this.track.scrollLeft / w; // 0 .. n-1, continuous
    if (this.progress) this.progress.style.transform = `translateX(${(pos * 100).toFixed(2)}%)`;
    const i = Math.round(pos);
    if (i !== this.index) this.setIndex(Math.min(Math.max(i, 0), n - 1));
  }

  goTo(i) {
    const vis = this.visible(); if (i < 0 || i >= vis.length) return;
    const behavior = reduce.matches ? 'auto' : 'smooth';
    if (stacked.matches) vis[i].scrollIntoView({ behavior, block: 'start' });
    else this.track.scrollTo({ left: i * this.track.clientWidth, behavior });
  }

  // fetch the next photo before it is needed, so a swipe never shows a blank
  warm(i) {
    const img = this.visible()[i]?.querySelector('img');
    if (img && img.loading === 'lazy') img.loading = 'eager';
  }

  // a colour's first photo starts loading when the visitor points at its swatch
  prewarm(key) {
    const f = this.figures.find((x) => x.dataset.fpColor === handleize(key));
    const img = f?.querySelector('img'); if (img && img.loading === 'lazy') img.loading = 'eager';
  }

  setFor(key) {
    const k = handleize(key);
    const mapped = this.figures.some((f) => f.dataset.fpColor);
    if (!mapped || !k) return this.figures;
    const set = this.figures.filter((f) => f.dataset.fpShared === 'true' || !f.dataset.fpColor || f.dataset.fpColor === k);
    return set.length ? set : this.figures;
  }

  async showColour(key, { animate = true } = {}) {
    const next = this.setFor(key);
    const now = this.visible();
    if (next.length === now.length && next.every((f, i) => f === now[i])) return;
    const token = ++this.token;
    const oldFig = stacked.matches ? now[0] : now[this.index];
    const oldImg = oldFig?.querySelector('img');
    const first = next[0]; const firstImg = first?.querySelector('img');
    if (firstImg) { firstImg.loading = 'eager'; firstImg.fetchPriority = 'high'; }
    this.ghost?.remove(); this.ghost = null;
    this.figures.forEach((f) => f.classList.remove('is-wiping', 'is-wiped'));

    const swap = () => {
      this.figures.forEach((f) => { f.hidden = !next.includes(f); });
      if (this.track) this.track.scrollLeft = 0;
      if (stacked.matches) {
        // the page scrolls inside .page-wrapper (never the window)
        const top = this.getBoundingClientRect().top;
        const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
        const scroller = document.querySelector('.page-wrapper') || document.scrollingElement;
        if (top < header) scroller.scrollBy({ top: top - header - 16, behavior: 'instant' });
      }
      this.refresh();
    };
    if (!animate || reduce.matches || !oldImg || !first) { swap(); return; }

    // the old photo stays on screen as a still while the new one is decoded, then the new one wipes in over it
    const ghost = document.createElement('div');
    ghost.className = 'fp-pdp__ghost'; ghost.setAttribute('aria-hidden', 'true');
    ghost.style.setProperty('--fp-photo-canvas', getComputedStyle(oldFig).getPropertyValue('--fp-photo-canvas') || '#fff');
    const g = new Image(); g.src = oldImg.currentSrc || oldImg.src; g.alt = ''; ghost.append(g);
    swap();
    const r = first.getBoundingClientRect(); const box = this.getBoundingClientRect();
    Object.assign(ghost.style, { left: `${r.left - box.left}px`, top: `${r.top - box.top}px`, width: `${r.width}px`, height: `${r.height}px` });
    this.append(ghost); this.ghost = ghost;
    first.classList.add('is-wiping');
    if (firstImg) await Promise.race([firstImg.decode().catch(() => {}), new Promise((res) => setTimeout(res, 600))]);
    if (token !== this.token) return;
    requestAnimationFrame(() => {
      first.classList.add('is-wiped');
      const done = () => { if (token !== this.token) return; first.classList.remove('is-wiping', 'is-wiped'); ghost.remove(); if (this.ghost === ghost) this.ghost = null; };
      first.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 1300);
    });
  }

  /* ---------- "+" cursor over the photos (fine pointers) ---------- */
  setupCursor() {
    if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const dot = document.createElement('span');
    dot.className = 'fp-pdp__cursor'; dot.setAttribute('aria-hidden', 'true');
    this.append(dot); this.cursor = dot;
    let frame = 0; let x = 0; let y = 0;
    const place = () => { frame = 0; dot.style.transform = `translate(${x}px, ${y}px)`; };
    this.track.addEventListener('pointermove', (e) => {
      const over = e.target.closest('[data-fp-enlarge]');
      dot.classList.toggle('is-on', Boolean(over));
      x = e.clientX; y = e.clientY;
      if (!frame) frame = requestAnimationFrame(place);
    });
    this.track.addEventListener('pointerleave', () => dot.classList.remove('is-on'));
  }

  /* ---------- full-screen view ---------- */
  setupLightbox() {
    const lb = this.lightbox; if (!lb) return;
    this.lbTrack = lb.querySelector('[data-fp-lightbox-track]');
    this.lbCount = lb.querySelector('[data-fp-lightbox-count]');
    lb.querySelector('[data-fp-lightbox-close]')?.addEventListener('click', () => lb.close());
    lb.querySelectorAll('[data-fp-lightbox-step]').forEach((b) => b.addEventListener('click', () => this.lbStep(Number(b.dataset.fpLightboxStep))));
    lb.addEventListener('keydown', (e) => { if (e.key === 'ArrowRight') this.lbStep(1); if (e.key === 'ArrowLeft') this.lbStep(-1); });
    lb.addEventListener('close', () => {
      document.documentElement.classList.remove('fp-lightbox-open'); lb.classList.remove('is-zoomed');
      this.lbReturn?.focus({ preventScroll: true });
    });
    this.lbTrack.addEventListener('click', (e) => {
      const slide = e.target.closest('.fp-pdp__lightbox-slide'); if (!slide) return;
      this.sharpen(slide);
      const on = !slide.classList.contains('is-zoomed');
      [...this.lbTrack.children].forEach((s) => s.classList.remove('is-zoomed'));
      slide.classList.toggle('is-zoomed', on);
      lb.classList.toggle('is-zoomed', on);
      if (on) this.pan(slide, e);
    });
    this.lbTrack.addEventListener('pointermove', (e) => {
      const slide = e.target.closest('.fp-pdp__lightbox-slide.is-zoomed'); if (slide) this.pan(slide, e);
    });
    let queued = false;
    this.lbTrack.addEventListener('scroll', () => {
      if (queued) return; queued = true;
      requestAnimationFrame(() => { queued = false; this.lbSync(); });
    }, { passive: true });
  }

  openLightbox(fig) {
    const lb = this.lightbox; if (!lb || !fig) return;
    const vis = this.visible(); const start = Math.max(0, vis.indexOf(fig));
    this.lbReturn = fig.querySelector('[data-fp-enlarge]');
    this.lbTrack.replaceChildren(...vis.map((f) => {
      const src = f.querySelector('img'); const large = f.querySelector('[data-fp-enlarge]')?.dataset.fpLarge;
      const slide = document.createElement('figure'); slide.className = 'fp-pdp__lightbox-slide'; slide.dataset.large = large || '';
      const low = new Image(); low.src = src?.currentSrc || src?.src || ''; low.alt = src?.alt || ''; low.className = 'fp-pdp__lightbox-low';
      slide.append(low); return slide;
    }));
    document.documentElement.classList.add('fp-lightbox-open');
    lb.showModal();
    this.lbTrack.scrollLeft = start * this.lbTrack.clientWidth;
    this.lbSync(true);
  }

  // the current photo and its neighbours get the large file, faded in over the low one
  sharpen(slide) {
    if (!slide || slide.dataset.sharp || !slide.dataset.large) return;
    slide.dataset.sharp = '1';
    const hi = new Image(); hi.alt = ''; hi.className = 'fp-pdp__lightbox-hi'; hi.decoding = 'async';
    hi.addEventListener('load', () => slide.classList.add('is-sharp'), { once: true });
    hi.src = slide.dataset.large; slide.append(hi);
  }

  pan(slide, e) {
    const r = slide.getBoundingClientRect();
    const px = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
    const py = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
    slide.style.setProperty('--fp-pan', `${px.toFixed(1)}% ${py.toFixed(1)}%`);
  }

  lbSync() {
    const slides = [...this.lbTrack.children]; const w = this.lbTrack.clientWidth || 1;
    const i = Math.min(slides.length - 1, Math.max(0, Math.round(this.lbTrack.scrollLeft / w)));
    if (i !== this.lbIndex) { slides.forEach((s) => s.classList.remove('is-zoomed')); this.lightbox.classList.remove('is-zoomed'); }
    [i, i + 1, i - 1].forEach((k) => this.sharpen(slides[k]));
    if (this.lbCount) this.lbCount.textContent = slides.length > 1 ? `${i + 1} / ${slides.length}` : '';
    this.lightbox.toggleAttribute('data-single', slides.length <= 1);
    this.lbIndex = i;
  }

  lbStep(d) {
    const slides = this.lbTrack.children.length; const i = Math.min(slides - 1, Math.max(0, (this.lbIndex || 0) + d));
    this.lbTrack.scrollTo({ left: i * this.lbTrack.clientWidth, behavior: reduce.matches ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('fp-pdp-gallery')) customElements.define('fp-pdp-gallery', PdpGallery);
